import React, { useEffect, useState } from 'react';
import { Loader2, User } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { rendersBeforeAuth } from '@/shared/layout/appShell';
import { loadPendingSocialSignup } from '@/utils/pendingSocialSignup';

interface AuthLoadingWrapperProps {
  children: React.ReactNode;
}

const AuthLoadingWrapper: React.FC<AuthLoadingWrapperProps> = ({ children }) => {
  // App shell waits on the AUTH check only — not user-data. Pages render their own
  // skeleton/loader while their data loads, so public pages never wait on a user fetch.
  const { isAuthLoading } = useEnhancedAuth();
  const { pathname } = useLocation();
  // A pending Keep-it redirect must not race Firebase's own redirect resolution, so it keeps the wait.
  const isLoading = isAuthLoading && (!rendersBeforeAuth(pathname) || loadPendingSocialSignup() != null);
  const [showTimeout, setShowTimeout] = useState(false);

  useEffect(() => {
    // Show timeout message after 3 seconds
    const timer = setTimeout(() => {
      if (isLoading) {
        setShowTimeout(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Reset timeout message when loading completes
  useEffect(() => {
    if (!isLoading) {
      setShowTimeout(false);
    }
  }, [isLoading]);

  // Show loading spinner while Firebase is determining auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-neutral-100 border border-neutral-200 rounded-xl shadow-ledge-lg p-8">
            <div className="w-16 h-16 bg-primary-50  rounded-full flex items-center justify-center mx-auto mb-6">
              <User className="w-8 h-8 text-primary-500 " />
            </div>

            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              {showTimeout ? 'Taking longer than expected...' : 'Loading Your Account'}
            </h2>

            <p className="text-neutral-600 mb-6">
              {showTimeout ? 'Please wait while we connect to your account' : 'Setting up your personalized experience...'}
            </p>

            <div className="flex items-center justify-center space-x-2 mb-6">
              <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              <span className="text-sm text-neutral-500">
                This will only take a moment...
              </span>
            </div>

            {/* Progress indicator */}
            <div className="w-full bg-neutral-200 rounded-full h-1.5 mb-6">
              <div
                className="bg-primary-500 h-1.5 rounded-full animate-pulse"
                style={{ width: showTimeout ? '90%' : '60%' }}
              />
            </div>

            {showTimeout && (
              <div className="space-y-3">
                <p className="text-sm text-neutral-500">
                  If this continues, try:
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors font-medium"
                  >
                    Refresh Page
                  </button>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="w-full px-4 py-2.5 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors font-medium"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthLoadingWrapper;